import { useCallback, useEffect, useRef, useState } from 'react';

import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { ContentDocument } from '@/lib/types';

interface UseTextDocOptions {
  contentId?: string;
  autosaveEnabled: boolean;
  debounceMs?: number;
}

/**
 * Manages a collaboratively editable content document.
 * Remote updates are adopted while locally clean. If local edits exist, a
 * version mismatch becomes an explicit conflict and autosave is suspended.
 */
export function useTextDoc({
  contentId,
  autosaveEnabled,
  debounceMs = 60000,
}: UseTextDocOptions) {
  const { novel } = useNovel();
  const { user, backend } = useFirebase();
  const { toast } = useToast();
  const [currentTextDoc, setCurrentTextDoc] = useState<ContentDocument | null>(null);
  const [editorContent, setEditorContentState] = useState('');
  const [lastSavedContent, setLastSavedContentState] = useState('');
  const [baseVersion, setBaseVersionState] = useState<number | null>(null);
  const [isLoadingText, setIsLoadingText] = useState(false);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [hasConflict, setHasConflict] = useState(false);
  const loadedContentIdRef = useRef<string | null>(null);
  const savingVersionRef = useRef<number | null>(null);
  const savingContentRef = useRef<string | null>(null);

  const novelRef = useRef(novel);
  novelRef.current = novel;
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const editorContentRef = useRef(editorContent);
  editorContentRef.current = editorContent;

  const lastSavedContentRef = useRef('');
  const baseVersionRef = useRef<number | null>(null);

  const isAutosavingRef = useRef(isAutosaving);
  isAutosavingRef.current = isAutosaving;
  const conflictReportedRef = useRef(false);

  const novelId = novel?.id;
  const ownerId = novel?.ownerId;
  const isDirty = editorContent !== lastSavedContent;

  const setEditorContent = useCallback((value: string) => {
    editorContentRef.current = value;
    setEditorContentState(value);
  }, []);

  const updateBaseVersion = useCallback((v: number | null) => {
    baseVersionRef.current = v;
    setBaseVersionState(v);
  }, []);

  const updateLastSavedContent = useCallback((content: string) => {
    lastSavedContentRef.current = content;
    setLastSavedContentState(content);
  }, []);

  const resetDocumentState = useCallback(() => {
    setCurrentTextDoc(null);
    setEditorContent('');
    updateLastSavedContent('');
    updateBaseVersion(null);
    loadedContentIdRef.current = null;
    setHasConflict(false);
    conflictReportedRef.current = false;
    savingVersionRef.current = null;
    savingContentRef.current = null;
  }, [setEditorContent, updateLastSavedContent, updateBaseVersion]);

  // Prevent old prose flashing while the requested document changes.
  const [prevContentId, setPrevContentId] = useState<string | undefined>(contentId);
  if (contentId !== prevContentId) {
    setPrevContentId(contentId);
    resetDocumentState();
    setIsLoadingText(true);
  }

  const layoutMode = novel?.storageLayout?.narrative?.contents?.items;

  useEffect(() => {
    if (!contentId || !backend || !novelId || !ownerId || !user || !novelRef.current) {
      resetDocumentState();
      return;
    }

    let isMounted = true;
    setIsLoadingText(true);

    const unsubscribe = backend.content.subscribeContent(
      ownerId,
      novelRef.current,
      contentId,
      (doc) => {
        if (!isMounted) return;
        if (!doc) {
          resetDocumentState();
          loadedContentIdRef.current = contentId;
          setIsLoadingText(false);
          return;
        }

        const docVersion = typeof doc.scratchpadVersion === 'number' && !isNaN(doc.scratchpadVersion)
          ? doc.scratchpadVersion
          : 0;

        setCurrentTextDoc(doc);
        const isInitialLoad = loadedContentIdRef.current !== contentId;
        const localIsDirty = editorContentRef.current !== lastSavedContentRef.current;
        const isMySavedVersion = (savingVersionRef.current !== null && docVersion === savingVersionRef.current) ||
          (savingContentRef.current !== null && doc.scratchpad === savingContentRef.current);
        const remoteMatchesEditor = doc.scratchpad === editorContentRef.current || isMySavedVersion;

        if (isInitialLoad || remoteMatchesEditor) {
          setEditorContentState(doc.scratchpad);
          updateLastSavedContent(doc.scratchpad);
          updateBaseVersion(docVersion);
          loadedContentIdRef.current = contentId;
          setHasConflict(false);
          conflictReportedRef.current = false;
        } else if (!localIsDirty && !isAutosavingRef.current) {
          // Safe live collaboration update: no local work can be overwritten.
          setEditorContentState(doc.scratchpad);
          updateLastSavedContent(doc.scratchpad);
          updateBaseVersion(docVersion);
          setHasConflict(false);
          conflictReportedRef.current = false;
        } else if (docVersion !== (baseVersionRef.current ?? 0)) {
          // Keep the local draft, but never autosave it over the collaborator.
          setHasConflict(true);
          if (!conflictReportedRef.current) {
            conflictReportedRef.current = true;
            toastRef.current({
              title: 'Editing conflict detected',
              description: 'Another collaborator saved this document. Your local draft was preserved, but autosave is paused.',
              variant: 'destructive',
            });
          }
        }
        setIsLoadingText(false);
      },
      (err) => {
        if (!isMounted) return;
        console.error('Failed to listen to text:', err);
        toastRef.current({ title: 'Error loading text content', variant: 'destructive' });
        setIsLoadingText(false);
      },
    );

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [contentId, backend, novelId, ownerId, user, layoutMode, resetDocumentState]);

  useEffect(() => {
    if (!autosaveEnabled || !isDirty || hasConflict || !contentId || !backend || !novelId || !ownerId || !user || !novelRef.current) return;

    const timer = setTimeout(async () => {
      const contentToSave = editorContentRef.current;
      const expectedVersion = baseVersionRef.current ?? 0;
      setIsAutosaving(true);
      try {
        savingVersionRef.current = expectedVersion + 1;
        savingContentRef.current = contentToSave;
        const didSave = await backend.content.saveContentVersion(
          ownerId,
          novelRef.current!,
          contentId,
          contentToSave,
          true,
          expectedVersion,
        );
        if (didSave) {
          updateLastSavedContent(contentToSave);
          updateBaseVersion(expectedVersion + 1);
        }
      } catch (error) {
        console.error('Autosave failed:', error);
        if (error instanceof Error && error.message.startsWith('CONTENT_VERSION_CONFLICT')) {
          setHasConflict(true);
          toastRef.current({
            title: 'Autosave paused',
            description: 'A collaborator saved a newer version. Your local draft has not been overwritten.',
            variant: 'destructive',
          });
        }
      } finally {
        setIsAutosaving(false);
        savingVersionRef.current = null;
        savingContentRef.current = null;
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [editorContent, lastSavedContent, autosaveEnabled, contentId, backend, novelId, ownerId, user, debounceMs, isDirty, hasConflict]);

  const reloadRemoteVersion = useCallback(() => {
    if (!currentTextDoc) return;
    setEditorContent(currentTextDoc.scratchpad);
    updateLastSavedContent(currentTextDoc.scratchpad);
    updateBaseVersion(currentTextDoc.scratchpadVersion);
    setHasConflict(false);
    conflictReportedRef.current = false;
  }, [currentTextDoc, setEditorContent, updateLastSavedContent, updateBaseVersion]);

  const persistCommentChange = useCallback(async (content: string) => {
    if (!contentId || !backend || !novelRef.current || !ownerId || !user) return;
    // Annotation markup belongs in the document but is not a prose version.
    setEditorContent(content);
    updateLastSavedContent(content);
    await backend.content.updateContent(ownerId, novelRef.current, contentId, {
      scratchpad: content,
    });
  }, [backend, contentId, ownerId, setEditorContent, updateLastSavedContent, user]);

  const saveManually = async (content?: string, silent = false, skipHistory = false) => {
    if (!contentId || !backend || !novel.id || !user) return;
    if (hasConflict) {
      toast({
        title: 'Cannot overwrite newer changes',
        description: 'Copy your local draft, reload the document, and merge the changes before saving.',
        variant: 'destructive',
      });
      return;
    }

    setIsAutosaving(true);
    const contentToSave = content ?? editorContent;
    const expectedVersion = baseVersionRef.current ?? 0;
    try {
      savingVersionRef.current = expectedVersion + 1;
      savingContentRef.current = contentToSave;
      const didSave = await backend.content.saveContentVersion(
        novel.ownerId,
        novel,
        contentId,
        contentToSave,
        false,
        expectedVersion,
        skipHistory,
      );
      if (didSave) {
        updateLastSavedContent(contentToSave);
        updateBaseVersion(expectedVersion + 1);
      }
      if (!silent) toast({ title: 'Saved successfully' });
    } catch (error) {
      console.error('Save failed:', error);
      if (error instanceof Error && error.message.startsWith('CONTENT_VERSION_CONFLICT')) {
        setHasConflict(true);
        toast({
          title: 'Editing conflict detected',
          description: 'Another collaborator saved first. Your local draft remains in the editor.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Error saving', variant: 'destructive' });
      }
    } finally {
      setIsAutosaving(false);
      savingVersionRef.current = null;
      savingContentRef.current = null;
    }
  };

  return {
    currentTextDoc,
    editorContent,
    setEditorContent,
    isLoadingText,
    isAutosaving,
    isDirty,
    hasConflict,
    reloadRemoteVersion,
    persistCommentChange,
    saveManually,
  };
}
